import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import NotifyDialog from "@/components/NotifyDialog";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import {
  Clock, MapPin, CalendarBlank, CarProfile, CheckCircle, Camera, Play, Stop, Timer,
} from "@phosphor-icons/react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const ETAS = [15, 30, 45, 60];

export default function MyDay() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [etaFor, setEtaFor] = useState(null);
  const [notify, setNotify] = useState(null); // {type, jobId, eta}
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadActive = () => api.timeActive().then(setActive).catch(() => {});
  const loadJobs = () => api.listJobs().then((all) => {
    const t = todayStr();
    setJobs(all.filter((j) => j.scheduled_date === t && j.status !== "cancelled"));
  }).catch(() => {});

  useEffect(() => { loadActive(); loadJobs(); }, []);

  const clockIn = async (jobId) => {
    setBusy(true);
    try { await api.clockIn({ job_id: jobId || null }); await loadActive(); toast.success("Clocked in"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not clock in"); }
    finally { setBusy(false); }
  };
  const clockOut = async () => {
    setBusy(true);
    try { await api.clockOut({}); await loadActive(); toast.success("Clocked out"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not clock out"); }
    finally { setBusy(false); }
  };

  const onMyWay = (job, eta) => { setEtaFor(null); setNotify({ type: "on_my_way", jobId: job.id, eta }); };

  const complete = async (job) => {
    try {
      await api.setJobStatus(job.id, "completed");
      await loadJobs();
      toast.success(`${job.number} marked complete`);
      setNotify({ type: "completed", jobId: job.id });
    } catch (e) { toast.error("Could not update job"); }
  };

  const uploadPhoto = async (job, file) => {
    if (!file) return;
    setUploading(job.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.uploadJobPhoto(job.id, fd);
      toast.success("Photo uploaded");
      await loadJobs();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(null); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto">
      <PageHeader title="My Day" subtitle="Your jobs, time & customer updates" />

      {/* Clock card */}
      <div className="bg-[#0B1726] text-white rounded-2xl p-5 mb-6 shadow-lg" data-testid="clock-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-widest"><Timer size={16} weight="duotone" className="text-[#00B4D8]" /> Time Clock</div>
            <div className="mt-1 text-lg font-display font-bold">
              {active ? <span className="text-emerald-400">On the clock{active.job_number ? ` · ${active.job_number}` : ""}</span> : <span className="text-slate-400">Clocked out</span>}
            </div>
          </div>
          {active ? (
            <Button onClick={clockOut} disabled={busy} className="bg-rose-500 hover:bg-rose-600 rounded-full h-14 px-6" data-testid="clock-out-btn">
              <Stop size={20} weight="fill" className="mr-1.5" /> Clock Out
            </Button>
          ) : (
            <Button onClick={() => clockIn(null)} disabled={busy} className="bg-[#00B4D8] hover:bg-cyan-600 rounded-full h-14 px-6" data-testid="clock-in-btn">
              <Play size={20} weight="fill" className="mr-1.5" /> Clock In
            </Button>
          )}
        </div>
      </div>

      <h2 className="font-display font-bold text-slate-800 mb-3 flex items-center gap-2"><CalendarBlank size={18} weight="duotone" className="text-[#00B4D8]" /> Today's Jobs</h2>

      {jobs.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center text-slate-400 text-sm">No jobs scheduled for today.</div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`myday-job-${job.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{job.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{job.client_name} · {job.number}</div>
                  {job.property_address && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={13} weight="duotone" />{job.property_address}</div>}
                  {job.scheduled_time && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Clock size={13} weight="duotone" />{job.scheduled_time}</div>}
                </div>
                <StatusBadge status={job.status} />
              </div>

              {/* ETA chips */}
              {etaFor === job.id && (
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`eta-chips-${job.id}`}>
                  <span className="text-xs text-slate-500">ETA:</span>
                  {ETAS.map((m) => (
                    <button key={m} onClick={() => onMyWay(job, m)} data-testid={`eta-${job.id}-${m}`}
                      className="text-sm font-semibold bg-[#00B4D8] hover:bg-cyan-600 text-white rounded-full px-3.5 py-1.5 transition-colors">
                      {m} min
                    </button>
                  ))}
                  <button onClick={() => setEtaFor(null)} className="text-xs text-slate-400 px-1">cancel</button>
                </div>
              )}

              <div className="mt-3 grid grid-cols-3 gap-2">
                <button onClick={() => setEtaFor(etaFor === job.id ? null : job.id)} data-testid={`on-my-way-${job.id}`}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg border border-slate-200 hover:border-[#00B4D8] hover:bg-cyan-50/50 text-slate-700 text-xs font-semibold transition-colors">
                  <CarProfile size={20} weight="duotone" className="text-[#00B4D8]" /> On my way
                </button>

                <label data-testid={`upload-${job.id}`}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg border border-slate-200 hover:border-[#00B4D8] hover:bg-cyan-50/50 text-slate-700 text-xs font-semibold cursor-pointer transition-colors">
                  <Camera size={20} weight="duotone" className="text-[#00B4D8]" />
                  {uploading === job.id ? "Uploading…" : "Add photo"}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadPhoto(job, e.target.files[0])} />
                </label>

                <button onClick={() => complete(job)} disabled={job.status === "completed"} data-testid={`complete-${job.id}`}
                  className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 disabled:opacity-40 text-slate-700 text-xs font-semibold transition-colors">
                  <CheckCircle size={20} weight="duotone" className="text-emerald-500" /> {job.status === "completed" ? "Done" : "Complete"}
                </button>
              </div>

              <div className="mt-2 flex items-center justify-between">
                {!active && (
                  <button onClick={() => clockIn(job.id)} className="text-xs text-[#00B4D8] font-semibold" data-testid={`clockin-job-${job.id}`}>Clock in on this job</button>
                )}
                <button onClick={() => setNotify({ type: "booked", jobId: job.id })} className="text-xs text-slate-500 hover:text-[#00B4D8] ml-auto" data-testid={`notify-booked-${job.id}`}>Email booking</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <NotifyDialog open={!!notify} type={notify?.type} jobId={notify?.jobId} etaMinutes={notify?.eta} onClose={() => setNotify(null)} />
    </div>
  );
}
